/*
 * Copyright © Cisco Systems, Inc. — All rights reserved.
 */

import { angular } from 'vms-platform';
import { ValidationService } from './lib/service';
import { normalizeType } from './lib/util';

// Built-in validators
import { validateCidrIpv4 } from './lib/validators/cidr-ipv4';
import { validateCidrIpv6 } from './lib/validators/cidr-ipv6';
import { validateCidr } from './lib/validators/cidr';
import { validateEmail } from './lib/validators/email';
import { validateFqdn } from './lib/validators/fqdn';
import { validateFreeText } from './lib/validators/free-text';
import { validateLocation } from './lib/validators/location';
import { validateInteger } from './lib/validators/integer';
import { validateInt32 } from './lib/validators/signed-32';
import { validateUint32 } from './lib/validators/unsigned-32';
import { validateIpAddress } from './lib/validators/ip-address';
import { validateIpv4 } from './lib/validators/ipv4';
import { validateIpv6 } from './lib/validators/ipv6';
import { validateLatitude } from './lib/validators/latitude';
import { validateLongitude } from './lib/validators/longitude';
import { validatePhoneNumber } from './lib/validators/phone-number';
import { validateEncsSerial } from './lib/validators/encs-serial';
import { validateSiteName } from './lib/validators/site-name';
import { validateSubnetMask } from './lib/validators/subnet-mask';

const MODULE_NAME = 'vms.validations';
export default MODULE_NAME;

// Register service and directive in angular DI module
const module = angular.module(MODULE_NAME, []);

module.factory('vms.validationService', () => new ValidationService());
module.value('vms.validations.normalizeType', normalizeType);

module.directive('vmsType', [
    'vms.validationService',
    function vmsTypeFactory(validationService) {
        function attachValidator(ngModel, type, validator) {
            if (validator.isAsync) {
                ngModel.$asyncValidators[type] = value => {
                    return ngModel.$isEmpty(value) || validator.validatorFunc(value);
                };
                return;
            }
            ngModel.$validators[type] = value => {
                return ngModel.$isEmpty(value) || validator.validatorFunc(value);
            };
        }

        function resolveRegexp(val, scope) {
            const checkExpression = /^([igsmx]{1,})?\/(?=.*?(\\w|\\d|\[.*?\]|\(.*?\))).*?\/([igsmx]{1,})?$/;

            // Lets see if the pattern itself is a regular expression.
            if (checkExpression.test(val)) {
                return val;
            }

            // Lets see if the item references a scope/controller variable.
            const parts = val.split(".");
            let curVal = scope;
            if (parts.length) {
                let i;
                for (i = 0; i < parts.length; i++) {
                    const part = parts[i];
                    const partVal = curVal[part];
                    curVal = partVal;
                    if (curVal == null || curVal === "") {
                        break;
                    }
                }
            }
            return curVal;
        }

        return {
            restrict: 'A',
            require: 'ngModel',
            link(scope, element, attrs, ngModel) {
                if (attrs.ngPattern) {
                    const regExp = resolveRegexp(attrs.ngPattern, scope);
                    if (regExp) {
                        return;
                    }
                    // We got to here, nothing found, so use the vms-Type checking.
                }

                // Special case of input box and support for msxPattern.
                scope.$watch("_msxFinalPattern", (newV) => {
                    if (newV && scope.__validatorAttached) {
                        scope.__validatorAttached = false;
                        const type = normalizeType(attrs.vmsType);
                        const desc = validationService._validatorDescFor(type);
                        if (desc) {
                            if (desc.isAsync) {
                                delete ngModel.$asyncValidators[type];
                            } else {
                                delete ngModel.$validators[type];
                            }
                            ngModel.$validators[type] = value => {
                                return ngModel.$isEmpty(value) || scope._msxFinalPattern && scope._msxFinalPattern.test ? scope._msxFinalPattern.test(value) : true;
                            };
                        }
                    }
                });
                scope.$watch("$ctrl.__msxPattern", (newV) => {
                    if (newV && scope.__validatorAttached) {
                        scope.__validatorAttached = false;
                        const type = normalizeType(attrs.vmsType);
                        const desc = validationService._validatorDescFor(type);
                        if (desc) {
                            if (desc.isAsync) {
                                delete ngModel.$asyncValidators[type];
                            } else {
                                delete ngModel.$validators[type];
                            }
                            ngModel.$validators[type] = value => {
                                return ngModel.$isEmpty(value) || scope._msxFinalPattern && scope._msxFinalPattern.test ? scope._msxFinalPattern.test(value) : true;
                            };
                        }
                    }
                });
                const type = normalizeType(attrs.vmsType);
                if (validationService.isRegistered(type)) {
                    scope.__validatorAttached = true;
                    attachValidator(
                        ngModel,
                        type,
                        validationService._validatorDescFor(type)
                    );
                }
            }
        };
    }
]);

// Register built-in validators
module.run([
    'vms.validationService',
    function registerTypes(validationService) {
        validationService.register('cidripv4', validateCidrIpv4);
        validationService.register('cidripv6', validateCidrIpv6);
        validationService.register(['cidr', 'cidrip'], validateCidr);
        validationService.register('email', validateEmail);
        validationService.register('fqdn', validateFqdn);
        validationService.register('freetext', validateFreeText);
        validationService.register('location', validateLocation);
        validationService.register('integer', validateInteger);
        validationService.register('int32', validateInt32);
        validationService.register('uint32', validateUint32);
        validationService.register(['ip', 'ipaddr', 'ipaddress'], validateIpAddress);
        validationService.register('ipv4', validateIpv4);
        validationService.register('ipv6', validateIpv6);
        validationService.register('latitude', validateLatitude);
        validationService.register('longitude', validateLongitude);
        validationService.register(['phone', 'phonenumber'], validatePhoneNumber);
        validationService.register('encsserial', validateEncsSerial);
        validationService.register('sitename', validateSiteName);
        validationService.register(['subnet', 'netmask'], validateSubnetMask);
    }
]);

// Export validator functions
export {
    validateCidrIpv4,
    validateCidrIpv6,
    validateCidr,
    validateEmail,
    validateFqdn,
    validateFreeText,
    validateLocation,
    validateInteger,
    validateInt32,
    validateUint32,
    validateIpAddress,
    validateIpv4,
    validateIpv6,
    validateLatitude,
    validateLongitude,
    validatePhoneNumber,
    validateEncsSerial,
    validateSiteName,
    validateSubnetMask
};
